import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";
import {
  FaArrowLeft,
  FaUser,
  FaFileAlt,
  FaFilePdf,
  FaFileImage,
  FaFileVideo,
  FaFileAudio,
  FaFileWord,
  FaExternalLinkAlt,
  FaChevronDown,
  FaChevronUp,
  FaPaperclip,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaEye,
  FaDownload,
  FaSpinner,
  FaTimes,
  FaBell,
  FaPaperPlane,
} from "react-icons/fa";

const STATUS_META = {
  approved: {
    label: "Approved",
    badge: "bg-green-100 text-green-700 border-green-200",
    icon: <FaCheckCircle className="text-green-600" />,
  },
  rejected: {
    label: "Rejected",
    badge: "bg-red-100 text-red-700 border-red-200",
    icon: <FaTimesCircle className="text-red-600" />,
  },
  pending: {
    label: "Pending",
    badge: "bg-amber-100 text-amber-700 border-amber-200",
    icon: <FaClock className="text-amber-500" />,
  },
};

const EventAttachmentDetail = () => {
  const { eventId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const apiDomain = process.env.REACT_APP_API_DOMAIN;

  const eventName = location.state?.eventName || `Event #${eventId}`;
  const requiredFiles = location.state?.requiredFiles || [];

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [remarkDrafts, setRemarkDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [preview, setPreview] = useState(null);
  const [zippingUser, setZippingUser] = useState(null);
  const [notifyTarget, setNotifyTarget] = useState(null);
  const [notifyTitle, setNotifyTitle] = useState("");
  const [notifyMsg, setNotifyMsg] = useState("");
  const [notifySending, setNotifySending] = useState(false);

  const fetchAttachments = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${apiDomain}/api/v2/attachment/event`, {
        params: { eventId },
      });
      setUsers(res.data?.users || []);
    } catch (err) {
      console.error("Error fetching event attachments:", err);
      setError("Failed to load participant attachments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttachments();
  }, [eventId, apiDomain]);

  const getFileIcon = (fileType) => {
    if (!fileType) return <FaFileAlt className="text-gray-400" />;
    const t = fileType.toUpperCase();
    if (t === "PDF") return <FaFilePdf className="text-red-500" />;
    if (["JPEG", "JPG", "PNG", "GIF", "WEBP"].includes(t)) return <FaFileImage className="text-green-500" />;
    if (["MP4", "MOV", "AVI", "WEBM"].includes(t)) return <FaFileVideo className="text-purple-500" />;
    if (["MP3", "WAV", "AAC"].includes(t)) return <FaFileAudio className="text-orange-500" />;
    if (["DOCX", "DOC"].includes(t)) return <FaFileWord className="text-blue-500" />;
    return <FaFileAlt className="text-gray-400" />;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "—";
    const d = new Date(ts * 1000);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
  };

  const isImage = (fileType) => !!fileType && ["JPEG", "JPG", "PNG", "GIF", "WEBP"].includes(fileType.toUpperCase());
  const isVideo = (fileType) => !!fileType && ["MP4", "MOV", "AVI", "WEBM"].includes(fileType.toUpperCase());
  const isPdf = (fileType) => !!fileType && fileType.toUpperCase() === "PDF";
  const canPreview = (fileType) => isImage(fileType) || isVideo(fileType) || isPdf(fileType);
  const statusOf = (att) => (att?.status || "pending").toLowerCase();

  const submitReview = async (user, idx, att, status) => {
    const key = `${user.userId}:${idx}`;
    setSavingKey(key);
    const remark = remarkDrafts[key] !== undefined ? remarkDrafts[key] : att.remark || "";
    try {
      await axios.post(`${apiDomain}/api/v2/attachment/review`, {
        eventId: String(eventId),
        userId: String(user.userId),
        attachmentIndex: idx,
        status,
        remark,
        reviewedBy: "admin",
        eventName,
        fileName: att.fileName || "",
      });
      setUsers((prev) => prev.map((u) => {
        if (u.userId !== user.userId) return u;
        const atts = Array.isArray(u.attachments) ? [...u.attachments] : [];
        if (atts[idx]) {
          atts[idx] = { ...atts[idx], status, remark, reviewedAt: Math.floor(Date.now() / 1000) };
        }
        return { ...u, attachments: atts };
      }));
    } catch (err) {
      console.error("Review save failed:", err);
      toast.error("Failed to save review.");
    } finally {
      setSavingKey(null);
    }
  };

  const downloadAllAsZip = async (user) => {
    const atts = Array.isArray(user.attachments) ? user.attachments : [];
    const withUrls = atts.filter((a) => a && a.fileUrl);
    if (withUrls.length === 0) return;
    setZippingUser(user.userId);
    try {
      const zip = new JSZip();
      await Promise.all(withUrls.map(async (att, i) => {
        try {
          const resp = await fetch(att.fileUrl);
          const blob = await resp.blob();
          zip.file(att.fileName || `file_${i+1}`, blob);
        } catch (e) { console.warn(e); }
      }));
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `${user.userName || user.userId}_docs.zip`);
    } finally { setZippingUser(null); }
  };

  const openNotify = (user, type = "") => {
    setNotifyTarget({ userId: user.userId, userName: user.userName || user.userId });
    if (type === "approved") {
      setNotifyTitle("Documents Approved! 🎉");
      setNotifyMsg(`Congratulations! Your documents for ${eventName} have been reviewed and approved.`);
    } else if (type === "rejected") {
      setNotifyTitle("Documents Need Changes ⚠️");
      setNotifyMsg(`We reviewed your documents for ${eventName} and found some issues. Please re-upload corrected files.`);
    } else {
      setNotifyTitle("");
      setNotifyMsg("");
    }
  };

  const sendNotification = async () => {
    if (!notifyTarget || !notifyMsg.trim()) return;
    setNotifySending(true);
    try {
      await axios.post(`${apiDomain}/api/v2/attachment/notify`, {
        userId: String(notifyTarget.userId),
        title: notifyTitle.trim(),
        body: notifyMsg.trim(),
      });
      setNotifyTarget(null);
      toast.success("Notification sent.");
    } finally { setNotifySending(false); }
  };

  const filteredUsers = users.filter((u) => (u.userName || u.userId || "").toLowerCase().includes(searchQuery.toLowerCase()));
  const reviewSummary = (atts) => {
    const counts = { approved: 0, rejected: 0, pending: 0 };
    (atts || []).forEach((a) => counts[statusOf(a)]++);
    const parts = [];
    if (counts.approved) parts.push(`${counts.approved} approved`);
    if (counts.rejected) parts.push(`${counts.rejected} rejected`);
    if (counts.pending) parts.push(`${counts.pending} pending`);
    return parts.join(" · ");
  };

  return (
    <div className="lg:mr-[1vw] p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/event-attachments")} className="p-2 rounded-lg hover:bg-gray-100 transition-colors"><FaArrowLeft className="text-gray-600" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{eventName}</h1>
          <p className="text-sm text-gray-500">Participant attachments · {users.length} users</p>
        </div>
      </div>

      {!loading && users.length > 0 && (
        <div className="relative mb-4">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search participants..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full md:w-80 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm outline-none" />
        </div>
      )}

      {loading && <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div></div>}

      <div className="space-y-3">
        {filteredUsers.map((user) => {
          const isExpanded = expandedUser === user.userId;
          const attachments = Array.isArray(user.attachments) ? user.attachments : [];
          const summary = reviewSummary(attachments);
          return (
            <div key={user.userId} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div onClick={() => setExpandedUser(isExpanded ? null : user.userId)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-2 rounded-full"><FaUser className="text-blue-600 text-sm" /></div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-800 flex items-center gap-2">
                      {user.userName || "Unknown User"}
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/participants/${user.userId}`); }} className="text-[10px] text-blue-600 hover:underline flex items-center gap-1"><FaExternalLinkAlt size={8} /> Profile</button>
                    </p>
                    <p className="text-xs text-gray-400">{user.userId}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="hidden lg:flex items-center gap-2 mr-4" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openNotify(user, "approved")} className="px-2 py-1 text-[10px] font-bold rounded border border-green-300 bg-green-50 text-green-700 hover:bg-green-100">Approve</button>
                    <button onClick={() => openNotify(user, "rejected")} className="px-2 py-1 text-[10px] font-bold rounded border border-red-300 bg-red-50 text-red-700 hover:bg-red-100">Reject</button>
                    <button onClick={() => openNotify(user)} className="px-2 py-1 text-[10px] font-bold rounded border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100">Notify</button>
                  </div>
                  {summary && <span className="text-xs text-gray-500 hidden md:inline">{summary}</span>}
                  <span className="text-sm text-gray-500">{attachments.length} files</span>
                  {isExpanded ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-100 px-5 py-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs text-gray-400">{user.updatedAt > 0 && `Last updated ${formatTimestamp(user.updatedAt)}`}</span>
                    <button onClick={() => downloadAllAsZip(user)} disabled={zippingUser === user.userId || attachments.length === 0} className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50">
                      {zippingUser === user.userId ? <FaSpinner className="animate-spin" /> : <FaDownload />} Download all (zip)
                    </button>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    {attachments.map((att, idx) => {
                      const st = statusOf(att);
                      const meta = STATUS_META[st] || STATUS_META.pending;
                      const key = `${user.userId}:${idx}`;
                      return (
                        <div key={idx} className="bg-white rounded-lg border border-gray-200 p-3">
                          {(isImage(att.fileType) || isVideo(att.fileType)) && att.fileUrl && (
                            <div className="relative mb-2 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 h-64 flex items-center justify-center cursor-pointer group" onClick={() => setPreview({ url: att.fileUrl, type: att.fileType, fileName: att.fileName })}>
                              {isImage(att.fileType) ? <img src={att.fileUrl} className="w-full h-full object-cover" /> : <div className="bg-black w-full h-full flex items-center justify-center"><FaFileVideo className="text-white/30 text-4xl" /></div>}
                              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><FaEye className="text-white text-3xl" /></div>
                            </div>
                          )}
                          <div className="flex items-start gap-2">
                            {getFileIcon(att.fileType)}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{att.fileName}</p>
                              <p className="text-xs text-gray-400">{att.fileType} · {formatTimestamp(att.uploadedAt)}</p>
                            </div>
                            <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border ${meta.badge}`}>{meta.label}</span>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            {canPreview(att.fileType) ? <button onClick={() => setPreview({ url: att.fileUrl, type: att.fileType, fileName: att.fileName })} className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">Preview</button> : <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">No Preview</span>}
                            <a href={att.fileUrl} target="_blank" className="text-xs text-gray-600 border border-gray-300 px-2 py-1 rounded">Download</a>
                          </div>
                          <textarea value={(remarkDrafts[key] ?? att.remark) || ""} onChange={(e) => setRemarkDrafts({...remarkDrafts, [key]: e.target.value})} placeholder="Remarks..." className="mt-3 w-full text-xs border border-gray-200 rounded p-2 outline-none" rows={2} />
                          <div className="mt-2 flex gap-2">
                            <button onClick={() => submitReview(user, idx, att, "approved")} className={`flex-1 py-1 rounded text-xs font-bold border ${st === "approved" ? "bg-green-600 text-white" : "text-green-600 border-green-600"}`}>Approve</button>
                            <button onClick={() => submitReview(user, idx, att, "rejected")} className={`flex-1 py-1 rounded text-xs font-bold border ${st === "rejected" ? "bg-red-600 text-white" : "text-red-600 border-red-600"}`}>Reject</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {notifyTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-4">
            <h3 className="font-bold mb-4">Notify {notifyTarget.userName}</h3>
            <input value={notifyTitle} onChange={e => setNotifyTitle(e.target.value)} placeholder="Title" className="w-full border p-2 rounded mb-3 text-sm" />
            <textarea value={notifyMsg} onChange={e => setNotifyMsg(e.target.value)} placeholder="Message" className="w-full border p-2 rounded mb-3 text-sm" rows={4} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setNotifyTarget(null)} className="px-4 py-1.5 border rounded text-sm">Cancel</button>
              <button onClick={sendNotification} disabled={notifySending || !notifyMsg.trim()} className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm font-bold">{notifySending ? "Sending..." : "Send"}</button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between p-3 border-b">
              <p className="font-bold truncate">{preview.fileName}</p>
              <button onClick={() => setPreview(null)}><FaTimes /></button>
            </div>
            <div className="flex-1 bg-gray-900 flex items-center justify-center overflow-auto">
              {isImage(preview.type) && <img src={preview.url} className="max-w-full max-h-full" />}
              {isVideo(preview.type) && <video src={preview.url} controls autoPlay className="max-w-full max-h-full" />}
              {isPdf(preview.type) && <iframe src={preview.url} className="w-full h-full" />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventAttachmentDetail;
